import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { ArrowLeft, Save, Map as MapIcon, Satellite, Layers, Search, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import "leaflet/dist/leaflet.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Draggable marker icon
const editIcon = new L.Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="#dc2626" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 1.996.471 3.889 1.309 5.574L12.5 41l11.191-22.926C24.529 16.389 25 14.496 25 12.5 25 5.596 19.404 0 12.5 0z"/><circle fill="white" cx="12.5" cy="12.5" r="5"/></svg>`),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const DraggableMarker = ({ position, setPosition }) => {
  const markerRef = useRef(null);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        setPosition([newPos.lat, newPos.lng]);
      }
    },
  };

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={editIcon}
    />
  );
};

const MapClickHandler = ({ setPosition }) => {
  useMapEvents({
    click: (e) => {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

// Component to move map to a location
const MapMover = ({ flyToPosition }) => {
  const map = useMap();
  
  useEffect(() => {
    if (flyToPosition) {
      map.flyTo(flyToPosition, 15, { duration: 1 });
    }
  }, [flyToPosition, map]);
  
  return null;
};

const EditCoordinates = () => {
  const { panoramaId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [panorama, setPanorama] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [position, setPosition] = useState([55.7558, 37.6173]); // Default Moscow
  const [mapType, setMapType] = useState("streets");
  
  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [flyToPosition, setFlyToPosition] = useState(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchPanorama();
  }, [panoramaId]);

  const fetchPanorama = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/panoramas`);
      const pano = response.data.find((p) => p.id === panoramaId);
      if (pano) {
        setPanorama(pano);
        if (pano.latitude && pano.longitude) {
          setPosition([pano.latitude, pano.longitude]);
        }
      } else {
        toast.error("Панорама не найдена");
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching panorama:", error);
      toast.error("Ошибка загрузки панорамы");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Search location using Nominatim (OpenStreetMap)
  const searchLocation = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: query,
            format: 'json',
            limit: 5,
            addressdetails: 1
          },
          headers: {
            'Accept-Language': 'ru'
          }
        }
      );
      setSearchResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 500);
  };

  // Handle selecting a search result
  const handleSelectResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setPosition([lat, lng]);
    setFlyToPosition([lat, lng]);
    setShowResults(false);
    setSearchQuery(result.display_name.split(',')[0]);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/panoramas/${panoramaId}/coordinates`,
        {
          lat: position[0],
          lng: position[1],
        },
        { headers: getAuthHeader() }
      );
      toast.success("Координаты сохранены");
      // Return to the game page if panorama has game_id
      if (panorama?.game_id) {
        navigate(`/${panorama.game_id}`);
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error saving coordinates:", error);
      toast.error(error.response?.data?.detail || "Ошибка сохранения координат");
    } finally {
      setSaving(false);
    }
  };
  
  const handleBack = () => {
    // Return to the game page if panorama has game_id
    if (panorama?.game_id) {
      navigate(`/${panorama.game_id}`);
    } else {
      navigate("/");
    }
  };

  const getTileLayerUrl = () => {
    switch (mapType) {
      case "satellite":
        return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
      case "hybrid":
        return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
      default:
        return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    }
  };

  if (loading) {
    return (
      <div className="edit-coords-container">
        <div className="loading-state">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="edit-coords-container" data-testid="edit-coordinates">
      {/* Back button - bottom left */}
      <Button
        onClick={handleBack}
        variant="outline"
        className="edit-coords-back-btn"
        data-testid="back-btn"
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 1000,
          background: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        <ArrowLeft size={18} />
        Назад
      </Button>

      {/* Save button - bottom right */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="edit-coords-save-btn"
        data-testid="save-btn"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          background: '#10b981',
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        {saving ? (
          "Сохранение..."
        ) : (
          <>
            <Save size={18} />
            Сохранить
          </>
        )}
      </Button>

      <div className="edit-coords-map">
        {/* Search Bar - Top Center */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: '90%',
          maxWidth: '500px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              color: '#666'
            }}>
              {searching ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Поиск места..."
              style={{
                flex: 1,
                border: 'none',
                padding: '12px 0',
                fontSize: '16px',
                outline: 'none'
              }}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                style={{
                  padding: '12px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                <X size={20} />
              </button>
            )}
          </div>
          
          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div style={{
              marginTop: '4px',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {searchResults.map((result, index) => (
                <div
                  key={result.place_id || index}
                  onClick={() => handleSelectResult(result)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                    {result.display_name.split(',')[0]}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {result.display_name.split(',').slice(1, 4).join(',')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map Type Selector - Top Left */}
        <div className="edit-map-type-selector">
          <button
            className={mapType === "streets" ? "active" : ""}
            onClick={() => setMapType("streets")}
            data-testid="edit-map-type-streets"
            title="Схема"
          >
            <MapIcon size={20} />
          </button>
          <button
            className={mapType === "satellite" ? "active" : ""}
            onClick={() => setMapType("satellite")}
            data-testid="edit-map-type-satellite"
            title="Спутник"
          >
            <Satellite size={20} />
          </button>
          <button
            className={mapType === "hybrid" ? "active" : ""}
            onClick={() => setMapType("hybrid")}
            data-testid="edit-map-type-hybrid"
            title="Гибрид"
          >
            <Layers size={20} />
          </button>
        </div>

        <MapContainer
          center={position}
          zoom={13}
          className="leaflet-map-full"
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            key={mapType}
            url={getTileLayerUrl()}
            maxZoom={19}
          />
          
          {mapType === "hybrid" && (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              opacity={0.3}
              maxZoom={19}
            />
          )}
          
          <DraggableMarker position={position} setPosition={setPosition} />
          <MapClickHandler setPosition={setPosition} />
          <MapMover flyToPosition={flyToPosition} />
        </MapContainer>
      </div>
    </div>
  );
};

export default EditCoordinates;
